import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Globe2, Lock, AlertTriangle, ChevronDown, ChevronUp, Loader2 } from "lucide-react";
import { getFxExposure, money } from "@/lib/apiClient";
import { cn } from "@/lib/utils";

const thisMonth = () => new Date().toISOString().slice(0, 7);
const monthLabel = (m) => new Date(`${m}-01T00:00:00Z`).toLocaleDateString("en-GB", { month: "long", year: "numeric", timeZone: "UTC" });
const fmtRate = (r) => (r == null ? "—" : Number(r).toFixed(4));

const CurrencyRow = ({ c, base, open, onToggle, onOpen }) => (
  <div className="rounded-lg border border-border" data-testid={`fx-row-${c.currency}`}>
    <button className="flex w-full flex-wrap items-center gap-x-4 gap-y-1 px-3 py-2.5 text-left hover:bg-muted/50" onClick={onToggle} data-testid={`fx-row-toggle-${c.currency}`}>
      <span className="font-mono text-sm font-bold w-12">{c.currency}</span>
      <span className="text-xs text-muted-foreground w-20">{c.count} invoice{c.count === 1 ? "" : "s"}</span>
      <span className="font-mono text-sm">{money(c.total, c.currency)}</span>
      <span className="text-xs text-muted-foreground">≈</span>
      <span className="font-mono text-sm font-semibold" data-testid={`fx-row-base-${c.currency}`}>{money(c.base_total, base)}</span>
      <span className="ml-auto flex items-center gap-2 text-xs text-muted-foreground">
        <span data-testid={`fx-row-rate-${c.currency}`}>
          rate {fmtRate(c.rate_avg)}{c.rate_min != null && c.rate_min !== c.rate_max && <span className="opacity-70"> ({fmtRate(c.rate_min)}–{fmtRate(c.rate_max)})</span>}
        </span>
        {c.locked > 0 && <span className="inline-flex items-center gap-0.5 text-sky-500" title="Rates locked by a reviewer"><Lock className="h-3 w-3" /> {c.locked}</span>}
        {c.unconverted > 0 && <span className="inline-flex items-center gap-0.5 text-amber-500" title="No rate available yet"><AlertTriangle className="h-3 w-3" /> {c.unconverted}</span>}
        {open ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
      </span>
    </button>
    {open && (
      <div className="border-t border-border divide-y divide-border" data-testid={`fx-row-invoices-${c.currency}`}>
        {c.invoices.map((i) => (
          <button key={i.id} onClick={() => onOpen(i.id)} className="flex w-full flex-wrap items-center gap-x-3 gap-y-0.5 px-3 py-2 text-left text-xs hover:bg-muted/50" data-testid={`fx-invoice-${i.id}`}>
            <span className="font-medium truncate max-w-[14rem]">{i.supplier}</span>
            <span className="font-mono text-muted-foreground">{i.invoice_number}</span>
            <span className="text-muted-foreground">{i.invoice_date}</span>
            <span className="ml-auto font-mono">{money(i.total, c.currency)}</span>
            <span className="font-mono text-muted-foreground">≈ {i.base_total != null ? money(i.base_total, base) : "—"}</span>
            <span className={cn("font-mono w-28 text-right", i.locked ? "text-sky-500" : "text-muted-foreground")}>
              {i.fx_rate ? `@ ${fmtRate(i.fx_rate)}` : "no rate"}{i.locked && <Lock className="inline h-3 w-3 ml-1" />}
            </span>
          </button>
        ))}
      </div>
    )}
  </div>
);

export const FxExposure = () => {
  const navigate = useNavigate();
  const [month, setMonth] = useState(thisMonth);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(true);
  const [open, setOpen] = useState(null);
  const [months, setMonths] = useState([]);

  useEffect(() => {
    let live = true;
    setBusy(true);
    getFxExposure(month).then((d) => { if (!live) return; setData(d); setMonths((prev) => (prev.length ? prev : d.months_with_foreign || [])); }).catch(() => live && setData(null)).finally(() => live && setBusy(false));
    return () => { live = false; };
  }, [month]);

  return (
    <div className="rounded-xl border border-border bg-card p-5" data-testid="fx-exposure">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <div className="flex items-center gap-2 text-sm font-medium"><Globe2 className="h-4 w-4 text-primary" /> Foreign-currency exposure · {monthLabel(month)}</div>
          {data && (
            <div className="text-xs text-muted-foreground mt-0.5" data-testid="fx-exposure-summary">
              {data.foreign_count} invoice{data.foreign_count === 1 ? "" : "s"} in {data.currencies.length} currenc{data.currencies.length === 1 ? "y" : "ies"} ≈ <span className="font-mono font-semibold text-foreground">{money(data.foreign_base_total, data.base_currency)}</span>
              {" "}· {data.share_pct}% of this month's spend · rates: {data.source}
              {data.unconverted > 0 && <span className="text-amber-500"> · {data.unconverted} without a rate yet</span>}
            </div>
          )}
        </div>
        <MonthSelect month={month} setMonth={setMonth} months={months} />
      </div>
      {busy && !data && <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>}
      {data && data.currencies.length === 0 && (
        <p className="mt-3 text-xs text-muted-foreground" data-testid="fx-exposure-empty">
          No non-{data.base_currency} invoices in {monthLabel(month)} — everything was billed in {data.base_currency}.
          {months.length > 0 && months[0].month !== month && <> Latest foreign-currency activity: <button className="text-primary hover:underline" onClick={() => setMonth(months[0].month)} data-testid="fx-jump-latest">{monthLabel(months[0].month)} ({months[0].count})</button>.</>}
        </p>
      )}
      {data && data.currencies.length > 0 && (
        <div className={cn("mt-3 space-y-2", busy && "opacity-60")}>
          {data.currencies.map((c) => (
            <CurrencyRow key={c.currency} c={c} base={data.base_currency} open={open === c.currency} onToggle={() => setOpen(open === c.currency ? null : c.currency)} onOpen={(id) => navigate(`/invoice/${id}`)} />
          ))}
        </div>
      )}
    </div>
  );
};

const MonthSelect = ({ month, setMonth, months }) => {
  const opts = [...new Map([[thisMonth(), 0], ...months.map((m) => [m.month, m.count])]).entries()].sort((a, b) => (a[0] < b[0] ? 1 : -1));
  return (
    <select value={month} onChange={(e) => setMonth(e.target.value)} className="h-8 rounded-lg border border-border bg-background px-2 text-xs focus:outline-none focus:ring-2 focus:ring-primary/30" data-testid="fx-month-select" aria-label="Month">
      {opts.map(([m, n]) => <option key={m} value={m}>{monthLabel(m)}{n ? ` · ${n}` : ""}</option>)}
    </select>
  );
};
